import { Routes, Route } from "react-router-dom";
import FeedbackPage from "./pages/feedback";
import Login from "./pages/login";
import SettingsPrivacy from "./pages/setting/settings";
import SelectAudience from "./pages/setting/SelectAudience";
import ContactInfo from "./pages/setting/ContactInfo";
import AccountOwnership from "./pages/setting/AccountOwnership";
import DisplayAccessibilityPage from "./pages/display/DisplayAccessibility";
import Profile from "./pages/profile";
import Home from "./pages/home";
import PostDetail from "./pages/post";
import NotificationsPage from "./pages/notifications";
import LoggedInRoutes from "./routes/LoggedInRoutes";
import NotLoggedInRoutes from "./routes/NotLoggedInRoutes";
import { useSelector, useDispatch } from "react-redux";
import Activate from "./pages/home/activate";
import CreatePostPopup from "./components/createPostPopup";
import { useEffect, useReducer, useState, useRef, useCallback } from "react";
import axios from "axios";
import DotLoader from "react-spinners/DotLoader";
import { postsReducer } from "./functions/reducers";
import Friends from "./pages/friends";
import Market from "./pages/market/market";
import Watch from "./pages/Watch/Watch";
import { ToastProvider } from "./components/ToastContainer";
import { NotificationProvider } from "./context/NotificationContext";
import { MessageNotificationProvider } from "./context/MessageNotificationContext";
import { SocketProvider } from "./context/SocketContext";
import RealtimeIndicator from "./components/RealtimeIndicator";
import CallDebugger from "./pages/messenger/CallDebugger";
import MessengerSidebar from "./components/MessengerSidebar";
import Header from "./components/header";
import AboutUs from "./components/AboutUs";
import SavedPosts from "./components/savedPosts/SavedPosts";
import ArchivedPosts from "./components/savedPosts/ArchivedPosts";
import Privacy from "./pages/privacy";
import Terms from "./pages/terms";
import Pricing from "./pages/pricing";
// import Cookies from "./pages/cookies";
import MobileNav from "./components/MobileNavigation";
import "./mobile-responsive.css"; // Import mobile responsiveness styles
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import AIChat from "./components/ai-chat/AIChat";
import FeedSocketListener from "./components/FeedSocketListener";
import MobileBackHandler from "./components/MobileBackHandler";


function App() {
  const [visible, setVisible] = useState(false);
  const [initialImages, setInitialImages] = useState([]);
  const [initialVideos, setInitialVideos] = useState([]);
  const homeMediaInputRef = useRef(null);

  const openWithPicker = () => {
    if (homeMediaInputRef.current) {
      homeMediaInputRef.current.value = "";
      homeMediaInputRef.current.click();
    }
  };

  const handleHomeMediaChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const readFile = (file) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve({ file, dataUrl: ev.target.result });
        reader.readAsDataURL(file);
      });

    Promise.all(files.map(readFile)).then((results) => {
      const imgs = [];
      const vids = [];
      results.forEach(({ file, dataUrl }) => {
        const t = (file.type || "").toLowerCase();
        const name = (file.name || "").toLowerCase();
        const isImage =
          /image\/(jpeg|jpg|png|webp|gif|avif)/.test(t) ||
          /\.(jpe?g|png|webp|gif|avif)$/.test(name);
        const isVideo =
          /video\/(mp4|webm|ogg)/.test(t) ||
          /\.(mp4|webm|ogg)$/.test(name);
        if (isImage) imgs.push(dataUrl);
        else if (isVideo) vids.push(dataUrl);
      });
      if (!imgs.length && !vids.length) return;
      setInitialImages(imgs);
      setInitialVideos(vids);
      setVisible(true);
    });
  };
  const [showMessengerSidebar, setShowMessengerSidebar] = useState(false);
  const [pendingMessengerParticipantId, setPendingMessengerParticipantId] = useState(null);
  const [pendingMessengerConversationId, setPendingMessengerConversationId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const { user } = useSelector((state) => ({ ...state }));
  const reduxDispatch = useDispatch();

  const atozasSsoEnabled =
    String(process.env.REACT_APP_ATOZAS_SSO_ENABLED).toLowerCase() === "true";
  // Gate the very first paint until the ATOZAS /me restore resolves, so we
  // don't flash the login page right after a successful SSO redirect to "/".
  // If SSO is off, or a local session already exists, there's nothing to wait
  // for and we render immediately.
  const [ssoChecked, setSsoChecked] = useState(
    !atozasSsoEnabled || !!user?.token
  );

  // ATOZAS SSO: on startup, if no local session exists, ask the backend to
  // restore the application JWT session created during the ATOZAS OIDC flow.
  // The backend returns the SAME app JWT + profile via the ATOZAS session
  // cookie (credentials: true). If found we dispatch the normal LOGIN action,
  // so from here on the app uses its existing authentication flow unchanged.
  useEffect(() => {
    // The backend adds ?atozas=1 after a completed SSO redirect. Strip it
    // from the address bar immediately (even if a local session already
    // exists), and treat it as a deliberate new login so the
    // "explicitly logged out" guard cannot ignore the restored session.
    let freshSsoLogin = false;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("atozas") === "1") {
        freshSsoLogin = true;
        localStorage.removeItem("atozas_logged_out");
        params.delete("atozas");
        const query = params.toString();
        window.history.replaceState(
          {},
          "",
          `${window.location.pathname}${query ? `?${query}` : ""}${
            window.location.hash || ""
          }`
        );
      }
    } catch (e) {
      /* ignore URL/storage errors */
    }

    if (!atozasSsoEnabled || user?.token) {
      setSsoChecked(true);
      return;
    }

    // If the user explicitly logged out, do NOT auto-restore the SSO session.
    // The flag is cleared only when they deliberately start a new ATOZAS login.
    let explicitlyLoggedOut = false;
    try {
      explicitlyLoggedOut = localStorage.getItem("atozas_logged_out") === "1";
    } catch (e) {
      /* ignore storage errors */
    }
    if (explicitlyLoggedOut && !freshSsoLogin) {
      setSsoChecked(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL || ""}/auth/atozas/me`,
          { withCredentials: true, timeout: 8000 }
        );
        if (!cancelled && data?.authenticated && data?.user?.token) {
          reduxDispatch({ type: "LOGIN", payload: data.user });
        }
      } catch (err) {
        // No ATOZAS session (or SSO off) — silently keep existing behavior.
      } finally {
        if (!cancelled) setSsoChecked(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.token, reduxDispatch, atozasSsoEnabled]);

  // True only during the brief SSO restore window on first load.
  const ssoBootstrapping = atozasSsoEnabled && !user && !ssoChecked;

  // Check if device is mobile.
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keep a global flag so mobile hardware-back can detect open messenger reliably.
  useEffect(() => {
    window.__messengerSidebarOpen = !!showMessengerSidebar;
    return () => {
      window.__messengerSidebarOpen = false;
    };
  }, [showMessengerSidebar]);

  // Listen for messenger toggle events from other components
  useEffect(() => {
    const handleToggleMessenger = () => {
      setShowMessengerSidebar(prev => !prev);
    };

    const handleCloseMessenger = () => {
      setShowMessengerSidebar(false);
    };

    window.addEventListener('toggleMessenger', handleToggleMessenger);
    window.addEventListener('closeMessenger', handleCloseMessenger);
    return () => {
      window.removeEventListener('toggleMessenger', handleToggleMessenger);
      window.removeEventListener('closeMessenger', handleCloseMessenger);
    };
  }, []);

  // Listen for requests to open messenger for a specific participant
  useEffect(() => {
    const handleOpenMessenger = (e) => {
      const participantId = e?.detail?.participantId;
      const conversationId = e?.detail?.conversationId;
      if (participantId) {
        setPendingMessengerParticipantId(participantId);
      }
      if (conversationId) {
        setPendingMessengerConversationId(conversationId);
      }
      setShowMessengerSidebar(true);
    };

    window.addEventListener('openMessenger', handleOpenMessenger);
    return () => {
      window.removeEventListener('openMessenger', handleOpenMessenger);
    };
  }, []);

  // Open messenger chat from push notification deep link (?openConversation=...)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const conversationId = params.get("openConversation");
      if (!conversationId) return;

      setPendingMessengerConversationId(conversationId);
      setShowMessengerSidebar(true);

      params.delete("openConversation");
      const nextQuery = params.toString();
      const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", nextUrl);
    } catch (err) {
      // ignore URL parse errors
    }
  }, []);
  const [{ loading, posts }, dispatch] = useReducer(postsReducer, {
    loading: false,
    posts: [],
    error: "",
  });

  const getAllPosts = useCallback(async (silent = false) => {
    if (!user || !user.token) {
      console.error("User is not logged in or token is missing.");
      return;
    }
    try {
      if (!silent) {
        dispatch({ type: "POSTS_REQUEST" });
      }
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL || ""}/getAllposts`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      dispatch({
        type: "POSTS_SUCCESS",
        payload: data,
      });
    } catch (error) {
      console.error("Error fetching posts:", error);
      if (!silent) {
        dispatch({
          type: "POSTS_ERROR",
          payload: error.response?.data?.message || "Failed to fetch posts.",
        });
      }
    }
  }, [user]);

  const handlePostDeleted = useCallback(
    (postId) => {
      if (postId) {
        dispatch({ type: "REMOVE_POST", payload: postId });
      }
      getAllPosts(true);
    },
    [getAllPosts]
  );

  useEffect(() => {
    getAllPosts();
  }, [getAllPosts]);

  return (
    <ToastProvider>
      <NotificationProvider>
        <MessageNotificationProvider>
          <SocketProvider>
            <FeedSocketListener getAllPosts={getAllPosts} />
            <MobileBackHandler
              isCreatePostOpen={visible}
              onCloseCreatePost={() => setVisible(false)}
              isMessengerOpen={showMessengerSidebar}
            />
            <div>
        {visible && (
          <CreatePostPopup
            user={user}
            setVisible={setVisible}
            posts={posts}
            dispatch={dispatch}
            getAllPosts={getAllPosts}
            initialImages={initialImages}
            initialVideos={initialVideos}
            clearInitialMedia={() => { setInitialImages([]); setInitialVideos([]); }}
          />
        )}
        {ssoBootstrapping ? (
          <div
            className="sso_bootstrap_loader"
            style={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DotLoader color="#7B5CFF" size={48} />
          </div>
        ) : (
        <Routes>
        <Route element={<LoggedInRoutes />}>
          <Route path="/settings" element={<SettingsPrivacy />} />
          <Route path="/settings/audience" element={<SelectAudience />} />
          <Route path="/settings/contact-info" element={<ContactInfo />} />
          <Route path="/settings/account-ownership" element={<AccountOwnership />} />
          <Route path="/feedback" element={<FeedbackPage />} />
          <Route path="/display-accessibility" element={<DisplayAccessibilityPage />} />
          <Route path="/about-us" element={<AboutUs />} />
          {/* <Route path="/cookies" element={<Cookies />} /> */}
          <Route path="/saved-posts" element={<SavedPosts token={user?.token} />} />
          <Route path="/saved-publication" element={<SavedPosts token={user?.token} />} />
          <Route path="/archived-posts" element={<ArchivedPosts token={user?.token} />} />
          <Route path="/archived-publication" element={<ArchivedPosts token={user?.token} />} />
          <Route
            path="/profile"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/about"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/friends"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/photos"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/videos"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/tagged"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/highlights"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/archive"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/more"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/about"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/friends"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/photos"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/videos"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/tagged"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/highlights"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/archive"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/profile/:username/more"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Profile setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/friends"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Friends setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/friends/:type"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Friends setVisible={setVisible} getAllPosts={getAllPosts} />
              </>
            }
          />
          <Route
            path="/"
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
              <Home
                setVisible={setVisible}
                posts={posts}
                loading={loading}
                getAllPosts={getAllPosts}
                onPostDeleted={handlePostDeleted}
                openWithPicker={openWithPicker}
              />
              </>
            }
          />
          <Route path="/activate/:token" element={<Activate />} />
          <Route 
            path="/post/:postId" 
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
                <PostDetail />
              </>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
                <NotificationsPage />
              </>
            } 
          />
          <Route 
            path="/market" 
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
                <Market />
              </>
            } 
          />
          <Route 
            path="/electronics" 
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
                <Market />
              </>
            } 
          />
          <Route 
            path="/watch" 
            element={
              <>
                <Header 
                  showMessengerSidebar={showMessengerSidebar} 
                  onMessengerToggle={() => setShowMessengerSidebar(!showMessengerSidebar)} 
                />
                <Watch />
              </>
            } 
          />
          <Route path="/call-debug" element={<CallDebugger />} />
        </Route>
        {/* Public routes - accessible without login */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/pricing" element={<Pricing />} />
        
        <Route element={<NotLoggedInRoutes />}>
          <Route path="/login" element={<Login />} />
        </Route>
      </Routes>
        )}
        <RealtimeIndicator />
        {/* Hidden file input to select media from Home Photo/Video button */}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif,video/mp4,video/webm,video/ogg"
          multiple
          hidden
          ref={homeMediaInputRef}
          onChange={handleHomeMediaChange}
        />
        
        {/* Messenger Sidebar */}
        <MessengerSidebar 
          isOpen={showMessengerSidebar} 
          onClose={() => setShowMessengerSidebar(false)}
          isMobile={isMobile}
          pendingParticipantId={pendingMessengerParticipantId}
          pendingConversationId={pendingMessengerConversationId}
          onPendingHandled={() => {
            setPendingMessengerParticipantId(null);
            setPendingMessengerConversationId(null);
          }}
        />
        
        {/* Mobile Navigation */}
        <MobileNav />
        {/* PWA Install Prompt */}
        <PWAInstallPrompt />
      <AIChat />
    </div>
        </SocketProvider>
        </MessageNotificationProvider>
      </NotificationProvider>
    </ToastProvider>
  );
}
export default App;